// Local persistence — IndexedDB via idb
// Owns: Trips, Catches, Waypoints, Photos, OfflineMaps (metadata)
// Does NOT own: conflict resolution (that's PowerSync + Postgres)

import { openDB, IDBPDatabase } from 'idb';
import type { Trip, Catch, Waypoint, Photo, OfflineMap } from './world-model';

const DB_NAME = 'bluguide';
const DB_VERSION = 1;

let _db: IDBPDatabase | null = null;

async function getDB() {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Trips
      if (!db.objectStoreNames.contains('trips')) {
        const trips = db.createObjectStore('trips', { keyPath: 'id' });
        trips.createIndex('startedAt', 'startedAt');
      }
      // Catches
      if (!db.objectStoreNames.contains('catches')) {
        const catches = db.createObjectStore('catches', { keyPath: 'id' });
        catches.createIndex('tripId', 'tripId');
        catches.createIndex('caughtAt', 'caughtAt');
      }
      // Waypoints
      if (!db.objectStoreNames.contains('waypoints')) {
        const waypoints = db.createObjectStore('waypoints', { keyPath: 'id' });
        waypoints.createIndex('tripId', 'tripId');
      }
      // Photos (metadata only — binary in OPFS)
      if (!db.objectStoreNames.contains('photos')) {
        db.createObjectStore('photos', { keyPath: 'id' });
      }
      // Offline maps (download state)
      if (!db.objectStoreNames.contains('offline_maps')) {
        db.createObjectStore('offline_maps', { keyPath: 'id' });
      }
    },
  });
  return _db;
}

// — Trips —
export async function saveTrip(trip: Trip) {
  const db = await getDB();
  await db.put('trips', trip);
}
export async function getTrip(id: string): Promise<Trip | undefined> {
  const db = await getDB();
  return db.get('trips', id);
}
export async function getAllTrips(): Promise<Trip[]> {
  const db = await getDB();
  const trips = await db.getAll('trips');
  return trips.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}
export async function deleteTrip(id: string) {
  const db = await getDB();
  await db.delete('trips', id);
}

// — Catches —
export async function saveCatch(c: Catch) {
  const db = await getDB();
  await db.put('catches', c);
}
export async function getCatch(id: string): Promise<Catch | undefined> {
  const db = await getDB();
  return db.get('catches', id);
}
export async function getCatchesForTrip(tripId: string): Promise<Catch[]> {
  const db = await getDB();
  return db.getAllFromIndex('catches', 'tripId', tripId);
}
export async function getAllCatches(): Promise<Catch[]> {
  const db = await getDB();
  const catches = await db.getAll('catches');
  return catches.sort((a, b) => b.caughtAt.localeCompare(a.caughtAt));
}

// — Waypoints —
export async function saveWaypoint(w: Waypoint) {
  const db = await getDB();
  await db.put('waypoints', w);
}
export async function getWaypointsForTrip(tripId: string): Promise<Waypoint[]> {
  const db = await getDB();
  return db.getAllFromIndex('waypoints', 'tripId', tripId);
}
export async function getAllWaypoints(): Promise<Waypoint[]> {
  const db = await getDB();
  return db.getAll('waypoints');
}

// — Photos —
export async function savePhotoMeta(photo: Photo) {
  const db = await getDB();
  await db.put('photos', photo);
}
export async function getPhoto(id: string): Promise<Photo | undefined> {
  const db = await getDB();
  return db.get('photos', id);
}

// — Offline Maps —
export async function saveOfflineMap(map: OfflineMap) {
  const db = await getDB();
  await db.put('offline_maps', map);
}
export async function getOfflineMap(id: string): Promise<OfflineMap | undefined> {
  const db = await getDB();
  return db.get('offline_maps', id);
}
export async function getAllOfflineMaps(): Promise<OfflineMap[]> {
  const db = await getDB();
  return db.getAll('offline_maps');
}
export async function deleteOfflineMap(id: string) {
  const db = await getDB();
  await db.delete('offline_maps', id);
}
